"use client";

import { useEffect, useRef, useState } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import debounce from "lodash/debounce";

import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";

import styles from "./HomepageHero.module.scss";
import { HomepageHeroLayoutProps } from "./HomepageHero.types";
import HomepageHeroTabs from "./HomepageHeroTabs";

const TOP_HEIGHT = "clamp(470px, 65vh, 547px)";
const TRIGGER_POINT = 150;

const HomepageHeroDesktop = ({
  data,
  setCoreFade,
  newCore,
  titleSection,
  benefitsSection,
  imagesSection,
}: HomepageHeroLayoutProps) => {
  const { subheading, tabs, pill } = data;
  const imgRef = useRef<HTMLDivElement>(null);
  const dividerRef = useRef<HTMLDivElement>(null);
  const dividerHorRef = useRef<HTMLDivElement>(null);
  const benefitsRef = useRef<HTMLDivElement>(null);
  const [finished, setFinished] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [verticalFinished, setVerticalFinished] = useState(false);
  const finishedRef = useRef(false);
  const playingRef = useRef(false);

  finishedRef.current = finished;
  playingRef.current = playing;

  const imagesGrow = (duration = 0.8) =>
    gsap.to(imgRef.current, { transform: "scale(1)", duration, paused: true });

  const divHorizontal2button = (duration = 1) => {
    const benefitsW = benefitsRef.current?.offsetWidth ?? 0;
    return gsap.to(dividerHorRef.current, {
      width: `calc(50% + ${benefitsW + 4}px)`,
      duration,
      paused: true,
      onComplete: () => {
        setFinished(true);
        gsap.killTweensOf(dividerHorRef.current);
      },
    });
  };

  const triggerTimeline = (duration = 1) => {
    if (finishedRef.current || playingRef.current) return;
    setPlaying(true);
    gsap.to(dividerRef.current, {
      duration,
      ease: "none",
      height: "50%",
      onComplete: () => {
        setVerticalFinished(true);
        gsap.killTweensOf(dividerRef.current);
        imagesGrow().play();
        divHorizontal2button(duration).play();
      },
    });
  };
  const triggerTimelineRef = useRef(triggerTimeline);
  triggerTimelineRef.current = triggerTimeline;

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(min-width: 1280px)", () => {
      if (finishedRef.current) return;
      gsap.to(dividerRef.current, {
        height: "15%",
        ease: "none",
        onComplete: () => {
          gsap.killTweensOf(dividerRef.current);
          triggerTimelineRef.current();
        },
        scrollTrigger: {
          invalidateOnRefresh: true,
          trigger: ".badge-container",
          start: "top-=145 top",
          end: `top-=${TRIGGER_POINT} top`,
          scrub: 0.3,
        },
      });
    });
    return () => mm.revert();
  }, []);

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (window.innerWidth < 1280) return;
      if (finishedRef.current) {
        imagesGrow(0.2).play();
        divHorizontal2button(0.2).play();
        return;
      }
      if (playingRef.current) return;
      const rect = document.querySelector(".badge-container")?.getBoundingClientRect();
      if (rect && rect.top < TRIGGER_POINT) triggerTimelineRef.current();
      else if (window.innerHeight > 1499) triggerTimelineRef.current();
    }, 150);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth < 1280 || finishedRef.current || playingRef.current) return;
      if (window.scrollY > 55) {
        const dividerH = parseInt(dividerRef.current?.style?.height || "0", 10);
        if (dividerH <= 15) triggerTimelineRef.current();
      }
    };
    const debounced = debounce(handleScroll, 500);
    window.addEventListener("scroll", debounced);
    return () => {
      debounced.cancel();
      window.removeEventListener("scroll", debounced);
    };
  }, []);

  return (
    <div className="flex">
      <div className="flex w-1/2 flex-col min-h-[1634px]">
        <div className="flex w-full items-end" style={{ height: TOP_HEIGHT }}>
          <div className="w-[360px] mx-auto">
            <Picture src={pill} alt="" role="presentation" loading="eager" fetchPriority="high" />
          </div>
        </div>
        <div className="relative flex w-full items-center h-[1087px]">
          <div
            ref={dividerRef}
            className={cn(styles.divider, !verticalFinished && styles.playing)}
          />
          <div className="relative z-[1] w-full">
            <div
              ref={dividerHorRef}
              className={cn(styles.dividerHorizontal, verticalFinished && styles.finished)}
            />
            {imagesSection(imgRef)}
          </div>
        </div>
      </div>
      <div className="flex w-1/2 flex-col min-h-[1634px]">
        <div className="flex w-full items-end pb-[60px]" style={{ height: TOP_HEIGHT }}>
          <div className="w-full">{titleSection}</div>
        </div>
        <div className="flex flex-row items-center min-[1470px]:pr-[112px] h-[1087px]">
          <div ref={benefitsRef} className="benefits max-w-[469px] pr-12 box-content pt-40">
            <div className="max-w-[445px] pb-20">
              <TextElementNova text={subheading} element="h2" className="Title-20-Super-Normal" />
            </div>
            {benefitsSection}
          </div>
          <div
            className={cn(
              "min-w-[171px] self-center z-[1] transition-transform duration-500",
              newCore === "women" && styles.tabWomen,
              newCore === "balance" && styles.tabBalance,
              newCore === "postpartum" && styles.tabPostpartum
            )}
          >
            <HomepageHeroTabs tabs={tabs} setter={setCoreFade} active={newCore} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepageHeroDesktop;
